import { useEffect, useState, useSyncExternalStore } from 'react';
import { bereinigeHeuteMap, heuteWahlFuer, merkeHeuteWahl, type HeuteMap, type HeuteWahl, type HeuteTagWahl } from '../components/guide-heute';
import { bereinigeAnsichtMerk, merkeFuerNutzer, type GuideAnsichtMerk, type GuideAnsichtMerkMap } from './guide-ansicht-merk';

type UiMode = 'auto' | 'standard' | 'compact';
type UiPreferences = { gespraech: 'aemter' | 'assistent'; mode: UiMode; guideCollapsed: boolean | null; coachCollapsed: boolean | null; focus: boolean; guideAnsicht: GuideAnsichtMerkMap; heute: HeuteMap };
const KEY = 'cui-ui-preferences-v1';
const defaults: UiPreferences = { gespraech: 'assistent', mode: 'auto', guideCollapsed: null, coachCollapsed: null, focus: false, guideAnsicht: {}, heute: {} };
function read(): UiPreferences {
  try {
    const data = JSON.parse(localStorage.getItem(KEY) || '{}');
    return { gespraech: data.gespraech === 'aemter' ? 'aemter' : 'assistent', mode: ['auto', 'standard', 'compact'].includes(data.mode) ? data.mode : 'auto',
      guideCollapsed: typeof data.guideCollapsed === 'boolean' ? data.guideCollapsed : null,
      coachCollapsed: typeof data.coachCollapsed === 'boolean' ? data.coachCollapsed : null,
      focus: false, guideAnsicht: bereinigeAnsichtMerk(data.guideAnsicht), heute: bereinigeHeuteMap(data.heute) };
  } catch { return defaults; }
}
let current = read();
const listeners = new Set<() => void>();
function subscribe(fn: () => void) { listeners.add(fn); return () => { listeners.delete(fn); }; }
window.addEventListener('storage', event => {
  if (event.key === KEY || event.key === null) { current = { ...read(), focus: current.focus }; listeners.forEach(fn => fn()); }
});
export function setUiPreferences(patch: Partial<UiPreferences>) {
  current = { ...current, ...patch };
  try { localStorage.setItem(KEY, JSON.stringify({ ...current, focus: false })); } catch { /* Session remains usable. */ }
  listeners.forEach(fn => fn());
}
/** Was dieses Fenster fuer EINEN Nutzer gemerkt hat (Hinweis gesehen, zuletzt bewusst gewaehlte Ansicht) — leer, wenn nichts gemerkt oder der Speicher kaputt ist. */
export function leseGuideAnsichtMerk(nutzer: string): GuideAnsichtMerk | undefined { return current.guideAnsicht[nutzer]; }
/** Merkt Teile fuer EINEN Nutzer. Schlaegt das Schreiben in den Browserspeicher fehl, gilt es bis zum Neuladen; danach erscheint der Hinweis einmal wieder. */
export function setGuideAnsichtMerk(nutzer: string, teil: Partial<GuideAnsichtMerk>): void { setUiPreferences({ guideAnsicht: merkeFuerNutzer(current.guideAnsicht, nutzer, teil) }); }
/** Wie `leseGuideAnsichtMerk`, aber als Hook: aendert sich, wenn ein anderer Teil des Fensters (z. B. die Erklaerkarte) etwas fuer diesen Nutzer merkt. */
export function useGuideAnsichtMerk(nutzer: string): GuideAnsichtMerk | undefined { return useSyncExternalStore(subscribe, () => current.guideAnsicht[nutzer]); }
/** „Heute dran“: Rafaels Aenderungen fuer HEUTE (Tag + Nutzer). Von einem anderen Tag gilt nichts; nur Buehnen-IDs, nie Inhalte. */
export function setHeuteWahl(nutzer: string, tag: string, wahl: HeuteWahl): void { setUiPreferences({ heute: merkeHeuteWahl(current.heute, nutzer, tag, wahl) }); }
const KEIN_HEUTE: HeuteTagWahl | undefined = undefined;
const heuteCache = new Map<string, { quelle: HeuteTagWahl | undefined; tag: string; wahl: HeuteWahl }>();
export function useHeuteWahl(nutzer: string, tag: string): HeuteWahl {
  const quelle = useSyncExternalStore(subscribe, () => current.heute[nutzer] ?? KEIN_HEUTE);
  const alt = heuteCache.get(nutzer);
  if (alt && alt.quelle === quelle && alt.tag === tag) return alt.wahl;
  const wahl = heuteWahlFuer(current.heute, nutzer, tag);
  heuteCache.set(nutzer, { quelle, tag, wahl });
  return wahl;
}
const smallQuery = '(max-width: 1439px), (max-height: 799px)';
const narrowQuery = '(max-width: 1099px)';
/** Gleich IPAD_HOCH_ABFRAGE in components/GuideBuehneReiter.tsx. */
export const ipadHochQuery = '(pointer: coarse) and (orientation: portrait) and (max-width: 1099px)';
export function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const media = window.matchMedia(query);
    const change = () => setMatches(media.matches);
    change(); media.addEventListener('change', change);
    return () => media.removeEventListener('change', change);
  }, [query]);
  return matches;
}
export function useUiPreferences() {
  const prefs = useSyncExternalStore(subscribe, () => current);
  const small = useMediaQuery(smallQuery);
  const narrow = useMediaQuery(narrowQuery);
  const compact = prefs.mode === 'compact' || (prefs.mode === 'auto' && small);
  /* iPad hochkant hat der Guide-Reiter die volle Breite und Hoehe — dort stehen Kanzler und
   * Vizekanzler ohne eigene Wahl offen (Rafael, 03.10.2026: Kanzler bleiben im Reiter „Guide"). */
  const ipadHoch = useMediaQuery(ipadHochQuery);
  return { ...prefs, compact, narrow, coachHidden: !prefs.focus && (prefs.coachCollapsed ?? (compact && !ipadHoch)) };
}

/**
 * Ist der Kanzlerbereich (Kanzler + Vizekanzler) eingeklappt? EINE Regel fuer Spalte und Menue.
 * In „uebereinander" gilt die Vorgabe wie bisher (kompakt -> eingeklappt, solange nicht gewaehlt);
 * in „nebeneinander" nur die ausdrueckliche Wahl. „Nur Kanzler" klappt nie ein (sonst bliebe die
 * Spalte leer), „Nur Guide" hat keinen Kanzler.
 */
export function kanzlerEingeklappt(ui: { focus: boolean; coachCollapsed: boolean | null; coachHidden: boolean }, modus?: string): boolean {
  if (!modus || modus === 'uebereinander') return ui.coachHidden;
  if (modus === 'nebeneinander') return !ui.focus && ui.coachCollapsed === true;
  return false;
}

/** Root styling follows viewport changes without remounting workspaces. */
export function useUiAppearance() {
  const { compact } = useUiPreferences();
  useEffect(() => { document.documentElement.dataset.uiDensity = compact ? 'compact' : 'standard'; }, [compact]);
}
